import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService } from 'primeng/api';
import { NotificationTypes } from 'src/app/app.enums';
import { SyncProductsComponent } from 'src/app/components/gallery-components/sync-products/sync-products.component';
import { AuthService, ResponseObject } from 'src/app/Services/auth.service';
import { GalleryConsignmentProduct } from 'src/app/Services/consignment.service';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import { SquareSpaceService } from 'src/app/Services/square-space/square-space.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-square-space',
    templateUrl: './square-space.component.html',
    inputs: ['isSentByEditProfile'],
    standalone: false
})
export class SquareSpace implements OnInit {
  // ViewChild reference to SyncProductsComponent
  @ViewChild(SyncProductsComponent) syncProductsComponent!: SyncProductsComponent;
  // Component properties
  public squareSpaceSiteDetails: any[] = [];
  public isConnectSquareSpaceSuccessDialogVisible: boolean = false;
  public isSentByEditProfile: boolean = false;
  public Products: GalleryConsignmentProduct[] = [];
  public squareSpaceSyncProducts: boolean = false;

  constructor(
    private squareSpaceService: SquareSpaceService,
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService,
    private confirmationService: ConfirmationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    // Subscribe to query parameters changes
    this.activatedRoute.queryParams.subscribe((params) => {
      // If code and state parameters exist (SquareSpace callback)
      if (params['code'] && params['state']) {
        this.onSquareSpaceAccountCallBack(params['code'], params['state']);
      }
      // No query parameters
      else {
        console.log('Query parameters are empty.');
        if (this.isSentByEditProfile == false) {
          this.getSquareSpaceAccountDetails(); // Get SquareSpace account details
        }
      }
    });
  }

  /**
   * Initiates connection with SquareSpace account
   */
  public connectWithSquareSpaceAccount(): void {
    this.ngxSpinnerService.show();
    this.squareSpaceService.squareSpaceAppInstallationLinkToConnectWithSquareSpace().subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        if (response.Success) {
          console.log('SquareSpace account connection successful:', response);
          // Redirect to SquareSpace installation URL
          window.location.href = response.ResponseData!;
        } else {
          console.error('Unexpected response:', response);
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Failed to connect to SquareSpace account:', error);
        this.notificationsService.showNotification(
          this.transloco.translate('squareSpace.connectionFailed'),
          this.transloco.translate('squareSpace.connectionFailedMessage'),
          NotificationTypes.Error
        );
      }
    );
  }

  /**
   * Disconnects SquareSpace account with confirmation dialog
   * @param id - Optional account ID to disconnect
   */
public onDisconnectSquareSpaceAccount(id: string | null = null): void {
  this.confirmationService.confirm({
    message: this.transloco.translate('squareSpace.disconnectConfirm'),
    icon: 'pi pi-exclamation-triangle',
    accept: () => {
      this.ngxSpinnerService.show();
      this.squareSpaceService.onDisconnectSquareSpaceAccount(id).subscribe(
        (response) => {
          this.ngxSpinnerService.hide();
          if (response.Success) {
            this.getSquareSpaceAccountDetails(); // step 1: get updated accounts

            // step 2: refresh synced products
            if (this.syncProductsComponent) {
              this.syncProductsComponent.selectedStoreFilter = null; // ✅ clear filter
              this.syncProductsComponent.reloadPlatformProducts('SquareSpace'); // ✅ reload products
            }

            this.notificationsService.showNotification(
              this.transloco.translate('squareSpace.disconnected'),
              this.transloco.translate('squareSpace.disconnectedMessage'),
              NotificationTypes.Success
            );
          } else {
            console.warn('Unexpected response:', response);
          }
        },
        (error) => {
          this.ngxSpinnerService.hide();
          console.error('Failed to disconnect SquareSpace account:', error);
        }
      );
    },
    reject: () => {
      console.log('SquareSpace account disconnection was cancelled.');
    },
  });
}


  /**
   * Fetches SquareSpace account details
   */
  public getSquareSpaceAccountDetails(): void {
    this.ngxSpinnerService.show();
    this.squareSpaceService.getSquareSpaceAccountDetails().subscribe(
      (response: any) => {
        this.ngxSpinnerService.hide();
        if (response) {
          this.squareSpaceSiteDetails = response;
          this.squareSpaceSyncProducts = true;
          console.log('SquareSpace account details:', this.squareSpaceSiteDetails);
        } else {
          console.warn('No SquareSpace account details found.');
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error fetching SquareSpace account details:', error);
        this.notificationsService.showNotification(
          this.transloco.translate('squareSpace.fetchFailed'),
          this.transloco.translate('squareSpace.fetchFailedMessage'),
          NotificationTypes.Error
        );
      }
    );
  }

  /**
   * Closes the SquareSpace connection success dialog
   */
  public closeSquareSpaceConnectionSuccessDialog(): void {
    this.isConnectSquareSpaceSuccessDialogVisible = false;
  }

  /**
   * Handles SquareSpace account callback with authorization code
   * @param code - Authorization code from SquareSpace
   * @param state - State parameter for security validation
   */
  public onSquareSpaceAccountCallBack(code: string, state: string): void {
    this.ngxSpinnerService.show();
    this.squareSpaceService.onSquareSpaceAccountCallBack(code, state).subscribe(
      (res: ResponseObject) => {
        this.ngxSpinnerService.hide();
        if (res.Success) {
          this.isConnectSquareSpaceSuccessDialogVisible = true;
          this.notificationsService.showNotification(
            this.transloco.translate('squareSpace.success'),
            res.ResponseMessage,
            NotificationTypes.Success
          );
          // Clear query parameters from URL
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {},
            replaceUrl: true,
          });
        } else {
          this.notificationsService.showNotification(
            this.transloco.translate('squareSpace.error'),
            res.ResponseMessage,
            NotificationTypes.Error
          );
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error during SquareSpace account callback:', error);
      }
    );
  }
}